import { Component, OnInit } from '@angular/core';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import {
  KrCycle,
  KrCycleResult,
  KrSimulationResults,
  SimulationResponse,
  SimulationResults,
} from '../../../core/models/simulation.model';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import Highcharts from 'highcharts';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { HighchartsChartModule } from 'highcharts-angular';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { SimulationDirectionsTableComponent } from '../../simulation-directions-table/simulation-directions-table.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import { map, merge } from 'rxjs';
import { createBaseLinearChartOptions, createBaseLogChartOptions, createLineSeries, updateMultipleChartsSeriesVisibilitySmooth } from '../../../utils/chart/chart-config.util';
import { formatValue } from '../../../utils/format.util';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';

@Component({
  selector: 'app-relative-permeability-results',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatExpansionModule,
    HighchartsChartModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatTableModule,
    SimulationOverviewComponent,
    SimulationDirectionsTableComponent,
  ],
  templateUrl: './relative-permeability-results.component.html',
  styleUrl: './relative-permeability-results.component.scss',
  standalone: true,
})
export class RelativePermeabilityResultsComponent extends Destroyable(Object) implements OnInit {
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly componentId: string = 'kr-simulation-results';
  readonly primaryDrainageColor: string = '#4F81BD';
  readonly firstImbibitionColor: string = '#C0504D';
  readonly secondaryDrainageColor: string = '#8064A2';
  readonly waterPhaseColor: string = '#1E90FF';
  readonly oilPhaseColor: string = '#228B22';
  readonly defaultColor: string = '#4A4A4A';
  readonly cycles: KrCycle[] = ['pd', 'fi', 'sd'];
  simulationResponse: SimulationResponse<KrSimulationResults> | null = null;
  cyclesForm!: FormGroup;
  chart?: Highcharts.Chart;
  chartLog?: Highcharts.Chart;
  chartOptions!: Highcharts.Options;
  chartOptionsLog!: Highcharts.Options;
  displayedColumns: string[] = [];
  tableData: any[] = [];
  isLoading: boolean = false;
  capillaryPressure: string | null = null;
  parentSimulationId: string | null = null;

  get pageTitle(): string {
    const prefix =
      this.simulationResponse?.type === SimulatorType.UpscalingKr
        ? 'Upscaling - '
        : 'Subsample - ';
    return prefix + 'Relative Permeability Results';
  }

  get isUpscaling(): boolean {
    return this.simulationResponse?.type === SimulatorType.UpscalingKr;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly fb: FormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.initCyclesForm();

    const simulationId: string | null = this.route.snapshot.paramMap.get('id');
    if (simulationId) {
      this.loadSimulation(simulationId);
    }
  }

  openParentSimulation(): void {
    if (this.parentSimulationId) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/simulation-results/pc', this.parentSimulationId])
      );
      window.open(url, '_blank');
    }
  }

  get results(): KrSimulationResults[] | undefined {
    return this.simulationResponse?.results;
  }

  get simulationResult(): KrSimulationResults | undefined {
    return this.results?.[0];
  }

  get simulationDirection(): string {
    return this.simulationResult?.direction ?? '';
  }

  get pdKrResult(): KrCycleResult | undefined {
    return this.simulationResult?.result?.pdKrResult;
  }

  get fiKrResult(): KrCycleResult | undefined {
    return this.simulationResult?.result?.fiKrResult;
  }

  get sdKrResult(): KrCycleResult | undefined {
    return this.simulationResult?.result?.sdKrResult;
  }

  getCycleControl(cycle: KrCycle): FormControl {
    return this.cyclesForm.get(cycle) as FormControl;
  }

  isKrCurveDataValid(): boolean {
    return !!this.results && this.results.length > 0;
  }

  isSimulationResultValid(): boolean {
    return this.simulationResult?.status?.toLowerCase() === 'success';
  }

  isCycleResultExists(cycle: KrCycle): boolean {
    if (!this.simulationResult) return false;

    const cycleResult: KrCycleResult | undefined = this.getCycleResult(cycle);
    return Boolean(cycleResult?.letSw?.length && cycleResult?.letKrw?.length && cycleResult?.letKro?.length);
  }

  getCycleResult(cycle: KrCycle): KrCycleResult | undefined {
    switch (cycle) {
      case 'pd':
        return this.pdKrResult;
      case 'fi':
        return this.fiKrResult;
      case 'sd':
        return this.sdKrResult;
    }
  }

  saveChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    // Populate both charts after they're initialized
    if (this.chart && this.chartLog) {
      this.initializeChartSeries();
    }
  }

  saveChartLogInstance(chart: Highcharts.Chart): void {
    this.chartLog = chart;
    // Populate both charts after they're initialized
    if (this.chart && this.chartLog) {
      this.initializeChartSeries();
    }
  }

  private initializeChartSeries(): void {
    if (!this.chart || !this.chartLog) return;

    const cycleOrder: KrCycle[] = ['pd', 'fi', 'sd'];
    const phaseOrder: Array<'water' | 'oil'> = ['water', 'oil'];

    // Add all possible series to both charts upfront
    cycleOrder.forEach((cycle) => {
      if (this.isCycleResultExists(cycle)) {
        phaseOrder.forEach((phase) => {
          const series = this.createChartSeriesByCycleType(cycle, phase);
          this.chart!.addSeries(JSON.parse(JSON.stringify(series)), false);
          this.chartLog!.addSeries(JSON.parse(JSON.stringify(series)), false);
        });
      }
    });

    this.chart.redraw();
    this.chartLog.redraw();

    // Apply initial visibility based on form state
    this.updateChartSeriesVisibility();
  }

  getColumnStyle(columnName: string): Record<string, string> {
    return {
      'background': this.defineColorForTable(columnName),
      'width': `${100 / (this.displayedColumns.length || 1)}%`,
    };
  }

  downloadResults(): void {
    if (this.simulationResponse && this.checkIfAtLeastOneCycleSelected()) {
      const params: Record<string, boolean> = {
        ...this.getSelectedDirections(),
        ...this.getSelectedCycles(),
      };
      this.simulationsService.downloadResult(this.simulationResponse.id, params);
    }
  }

  private loadSimulation(id: string): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(id, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((simulation: SimulationResponse<SimulationResults>) => {
        this.simulationResponse = simulation as SimulationResponse<KrSimulationResults>;

        if (this.isKrCurveDataValid() && this.isSimulationResultValid()) {
          this.configureCyclesFormControls();
          this.initChart();
          this.updateTable();
        }

        // Load parent simulation (Capillary Pressure) if exists
        const parentSimulationId = this.simulationResponse?.parameters?.parentSimulationId;
        if (parentSimulationId) {
          this.parentSimulationId = parentSimulationId;
          this.loadParentSimulation(parentSimulationId);
        }
      });
  }

  private loadParentSimulation(parentSimulationId: string): void {
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(parentSimulationId, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((parentSimulation: SimulationResponse<SimulationResults>) => {
        this.capillaryPressure = parentSimulation.name;
      });
  }

  private configureCyclesFormControls(): void {
    this.cycles.forEach((cycle: KrCycle) => {
      // SD option is always disabled
      if (cycle === 'sd') {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
        return;
      }

      const cycleResultExists: boolean = this.isCycleResultExists(cycle);

      if (cycleResultExists) {
        this.getCycleControl(cycle).patchValue(true, { emitEvent: false });
        this.getCycleControl(cycle).enable({ emitEvent: false });
      } else {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
      }
    });
  }

  private initChart(): void {
    this.chartOptions = this.createLinearChart(
      'Relative Permeability (Kr)',
      'Saturation_fluid1 (fraction)',
      'Relative Permeability (fraction)',
    );
    this.chartOptionsLog = this.createLogChart(
      'Relative Permeability (Kr)',
      'Saturation_fluid1 (fraction)',
      'Relative Permeability (fraction)',
    );
  }

  private createLinearChart(
    title: string,
    xAxisTitle: string,
    yAxisTitle: string,
  ): Highcharts.Options {
    return createBaseLinearChartOptions({
      title,
      xAxisTitle,
      yAxisTitle,
      series: [], // Start with empty series
      xMin: 0,
      xMax: 1,
      yMin: 0,
      yMax: 1,
      tooltipFormatter: function() {
        return `<b>${this.series.name}</b><br/><b>Sw:</b> ${this.x?.toFixed(2)}<br/><b>Kr:</b> ${this.y?.toFixed(2)}`;
      },
    });
  }

  private createLogChart(
    title: string,
    xAxisTitle: string,
    yAxisTitle: string,
  ): Highcharts.Options {
    return createBaseLogChartOptions({
      title,
      xAxisTitle,
      yAxisTitle,
      series: [], // Start with empty series
      xAxisType: 'linear',
      yAxisType: 'logarithmic',
      xMin: 0,
      xMax: 1,
      tooltipFormatter: function() {
        return `<b>${this.series.name}</b><br/><b>Sw:</b> ${this.x?.toFixed(2)}<br/><b>Kr:</b> ${this.y?.toFixed(2)}`;
      },
    });
  }

  private initChartSeries(): Highcharts.SeriesOptionsType[] {
    const directionSeries: Highcharts.SeriesOptionsType[] = [];
    const isPDEnabled: boolean = this.getCycleControl('pd').value;
    const isFIEnabled: boolean = this.getCycleControl('fi').value;
    const isSDEnabled: boolean = this.getCycleControl('sd').value;

    if (isPDEnabled) {
      const waterSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('pd', 'water');
      const oilSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('pd', 'oil');
      directionSeries.push(waterSeries, oilSeries);
    }

    if (isFIEnabled) {
      const waterSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('fi', 'water');
      const oilSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('fi', 'oil');
      directionSeries.push(waterSeries, oilSeries);
    }

    if (isSDEnabled) {
      const waterSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('sd', 'water');
      const oilSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('sd', 'oil');
      directionSeries.push(waterSeries, oilSeries);
    }

    return directionSeries;
  }

  private createChartSeriesByCycleType(cycle: KrCycle, phase: 'water' | 'oil'): Highcharts.SeriesOptionsType {
    const cycleResult: KrCycleResult | undefined = this.getCycleResult(cycle);
    const cycleName: string = this.getCycleName(cycle);

    let data: number[][] = [];
    let name: string;
    let color: string;
    let markerSymbol: string;

    if (!cycleResult) {
      return {} as Highcharts.SeriesOptionsType;
    }

    const krValues: string[] = this.getKrValues(cycleResult, phase, this.simulationDirection);

    // Use cycle-specific colors for both water and oil phases
    color = this.defineColorForCycleType(cycle);

    if (phase === 'water') {
      name = `${cycleName} - Kr_fluid1`;
      markerSymbol = 'circle';
    } else {
      name = `${cycleName} - Kr_fluid2`;
      markerSymbol = 'triangle';
    }

    data = cycleResult.letSw.map((saturation: string, index: number) => {
      return [Number(saturation), Number(krValues[index])];
    });

    return createLineSeries({
      name,
      data,
      color,
      symbol: markerSymbol,
      markerEnabled: true,
      custom: {
        cycle,
        phase,
      },
    });
  }

  private getKrValues(cycleResult: KrCycleResult, phase: 'water' | 'oil', direction: string): string[] {
    // Always use LET correlation values
    if (phase === 'water') {
      return cycleResult.letKrw;
    } else {
      return cycleResult.letKro;
    }
  }

  private getCycleName(cycle: KrCycle): string {
    switch (cycle) {
      case 'pd':
        return 'Primary Drainage';
      case 'fi':
        return 'First Imbibition';
      case 'sd':
        return 'Secondary Drainage';
    }
  }

  private updateSeriesWhenSelectedCyclesChanged(cycle: KrCycle, checked: boolean): void {
    // Regenerate all series in consistent order instead of just adding/removing
    // This ensures series order always matches table order (pd water, pd oil, fi water, fi oil, sd water, sd oil)
    this.updateChartSeriesVisibility();
  }

  private updateChartSeriesVisibility(): void {
    if (!this.chart || !this.chartLog) return;

    const cycleOrder: KrCycle[] = ['pd', 'fi', 'sd'];
    const phaseOrder: Array<'water' | 'oil'> = ['water', 'oil'];

    // Determine which series should be visible based on selection
    const selectedSeriesNames = new Set<string>();
    cycleOrder.forEach((cycle) => {
      if (this.getCycleControl(cycle).value && this.isCycleResultExists(cycle)) {
        phaseOrder.forEach((phase) => {
          // Get the series name created by createChartSeriesByCycleType
          const seriesName = this.createChartSeriesByCycleType(cycle, phase).name;
          selectedSeriesNames.add(seriesName as string);
        });
      }
    });

    // Update both charts with visibility-based approach using smooth function
    updateMultipleChartsSeriesVisibilitySmooth(
      [this.chart, this.chartLog],
      selectedSeriesNames
    );
  }

  private updateTable(): void {
    const rows: any[] = [];
    const pdResult: KrCycleResult | undefined = this.getCycleControl('pd').value ? this.pdKrResult : undefined;
    const fiResult: KrCycleResult | undefined = this.getCycleControl('fi').value ? this.fiKrResult : undefined;
    const sdResult: KrCycleResult | undefined = this.getCycleControl('sd').value ? this.sdKrResult : undefined;

    const maxLength: number = Math.max(
      pdResult?.letSw?.length ?? 0,
      fiResult?.letSw?.length ?? 0,
      sdResult?.letSw?.length ?? 0,
    );

    for (let i = 0; i < maxLength; i++) {
      const row: Record<string, string> = {};

      if (pdResult) {
        row[`PD Saturation_fluid1 (fraction)`] = formatValue(pdResult.letSw[i]);
        const krwValues: string[] = this.getKrValues(pdResult, 'water', this.simulationDirection);
        const kroValues: string[] = this.getKrValues(pdResult, 'oil', this.simulationDirection);
        row[`PD Kr_fluid1 (fraction)`] = formatValue(krwValues[i]);
        row[`PD Kr_fluid2 (fraction)`] = formatValue(kroValues[i]);
      }

      if (fiResult) {
        row[`FI Saturation_fluid1 (fraction)`] = formatValue(fiResult.letSw[i]);
        const krwValues: string[] = this.getKrValues(fiResult, 'water', this.simulationDirection);
        const kroValues: string[] = this.getKrValues(fiResult, 'oil', this.simulationDirection);
        row[`FI Kr_fluid1 (fraction)`] = formatValue(krwValues[i]);
        row[`FI Kr_fluid2 (fraction)`] = formatValue(kroValues[i]);
      }

      if (sdResult) {
        row[`SD Saturation_fluid1 (fraction)`] = formatValue(sdResult.letSw[i]);
        const krwValues: string[] = this.getKrValues(sdResult, 'water', this.simulationDirection);
        const kroValues: string[] = this.getKrValues(sdResult, 'oil', this.simulationDirection);
        row[`SD Kr_fluid1 (fraction)`] = formatValue(krwValues[i]);
        row[`SD Kr_fluid2 (fraction)`] = formatValue(kroValues[i]);
      }

      rows.push(row);
    }

    this.displayedColumns = rows.length ? Object.keys(rows[0]) : [];
    this.tableData = rows;
  }

  private defineColorForCycleType(cycle: KrCycle): string {
    switch (cycle) {
      case 'pd':
        return this.primaryDrainageColor;
      case 'fi':
        return this.firstImbibitionColor;
      case 'sd':
        return this.secondaryDrainageColor;
    }
  }

  private defineColorForTable(columnName: string): string {
    const lowerName: string = columnName.toLowerCase();

    // Use cycle-specific colors for all columns
    const prefix: string = lowerName.slice(0, 2);
    switch (prefix) {
      case 'pd':
        return this.defineColorForCycleType('pd');
      case 'fi':
        return this.defineColorForCycleType('fi');
      case 'sd':
        return this.defineColorForCycleType('sd');
      default:
        return this.defaultColor;
    }
  }

  private getSelectedDirections(): Record<string, boolean> {
    return {
      directionX: this.simulationDirection.toLowerCase() === 'x',
      directionY: this.simulationDirection.toLowerCase() === 'y',
      directionZ: this.simulationDirection.toLowerCase() === 'z',
    };
  }

  private getSelectedCycles(): Record<string, boolean> {
    return {
      simulatePrimaryDrainage: this.getCycleControl('pd').value,
      simulateFirstImbibition: this.getCycleControl('fi').value,
      simulateSecondaryDrainage: this.getCycleControl('sd').value,
    };
  }

  private checkIfAtLeastOneCycleSelected(): boolean {
    const selectedCycles: Record<string, boolean> = this.getSelectedCycles();
    return Object.values(selectedCycles).some(Boolean);
  }

  private initCyclesForm(): void {
    this.cyclesForm = this.fb.group({
      pd: [{ value: false, disabled: true }],
      fi: [{ value: false, disabled: true }],
      sd: [{ value: false, disabled: true }],
    });

    merge(
      this.getCycleControl('pd').valueChanges.pipe(map(value => ({ cycle: 'pd', value }))),
      this.getCycleControl('fi').valueChanges.pipe(map(value => ({ cycle: 'fi', value }))),
      this.getCycleControl('sd').valueChanges.pipe(map(value => ({ cycle: 'sd', value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ cycle, value }) => {
        this.updateSeriesWhenSelectedCyclesChanged(cycle as KrCycle, value);
        this.updateTable();
      });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });
  }
}
